import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isNil, omitBy } from 'lodash';
import { startWith } from 'rxjs/operators';
import { mapSupplierUtilizationSortColumn } from '../../helpers/sort-column-mapping';
import { PayablesInput, SortDirection } from '../../model/models';
import { SellerSummaryGQL } from '../../model/summary-seller';
import { ListSellerSummaryRequest } from './list-seller-summary';

export class TopSuppliersByOpenBalanceResponse {
    public rows: TopSupplier[] = [];
    public totalCount = 0;
    constructor(obj?: Partial<TopSuppliersByOpenBalanceResponse>) {
        Object.keys(obj ?? {}).forEach((key) => (this[key] = obj[key]));
    }
}
export type TopSupplier = Pick<SellerSummaryGQL, 'name' | 'sellerKey' | 'openInvoiceAmount'>;
interface SupplierUtilznGQLResponse {
    data: {
        filteredSuppliers: {
            rows: TopSupplier[];
            totals: { supplierCount: number };
        };
        unfilteredSuppliers: {
            totals: { supplierCount: number };
        };
    };
}

@Injectable({ providedIn: 'root' })
export class TopSuppliersByOpenBalance extends ApiService<
    ListSellerSummaryRequest,
    TopSuppliersByOpenBalanceResponse
> {
    public requestType = ListSellerSummaryRequest;
    public responseType = TopSuppliersByOpenBalanceResponse;
    public endpointName = 'ListSellerSummary';
    public serviceName = 'financing';
    private payLaterIsOn = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this.payLaterIsOn = isOn;
                if (isOn) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'ListSellerSummary';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: ListSellerSummaryRequest
    ): { query: string } | ListSellerSummaryRequest {
        if (this.payLaterIsOn) {
            const { skip, take, sortColumn, sortDirection } = req.pagination;
            const input: PayablesInput = omitBy(
                {
                    companyFilter: {
                        buyerIds: req.refBuyerKeys ? req.refBuyerKeys : [],
                        supplierIds: []
                    },
                    pagination: {
                        skip: skip || 0,
                        take: take,
                        sortColumn: new EnumType(mapSupplierUtilizationSortColumn(sortColumn)),
                        sortDirection: new EnumType(
                            SortDirection[sortDirection] ?? SortDirection.DESCENDING
                        )
                    }
                },
                isNil
            );
            const money = { cents: true, currencyCode: true };
            const query = jsonToGraphQLQuery({
                filteredSuppliers: {
                    __aliasFor: 'supplierUtilizations',
                    __args: { input },
                    rows: {
                        name: true,
                        sellerKey: true,
                        openInvoiceAmount: money
                    },
                    totals: { supplierCount: true }
                },
                unfilteredSuppliers: {
                    __aliasFor: 'supplierUtilizations',
                    __args: {
                        input: {
                            ...input,
                            pagination: {
                                ...input.pagination,
                                skip: 0,
                                take: 1000
                            }
                        }
                    },
                    totals: { supplierCount: true }
                }
            });
            return { query: `{${query}}` };
        }
        return req;
    }

    public responseMapper(
        res: SupplierUtilznGQLResponse | TopSuppliersByOpenBalanceResponse
    ): TopSuppliersByOpenBalanceResponse {
        if (this.payLaterIsOn) {
            const { filteredSuppliers, unfilteredSuppliers } = (res as SupplierUtilznGQLResponse)
                .data;
            return new TopSuppliersByOpenBalanceResponse({
                rows: filteredSuppliers.rows.map((row) => ({
                    name: row.name,
                    sellerKey: row.sellerKey,
                    openInvoiceAmount: row.openInvoiceAmount
                })),
                totalCount: unfilteredSuppliers.totals.supplierCount
            });
        }
        return res as TopSuppliersByOpenBalanceResponse;
    }

    public get endpointUrl(): string {
        return this.payLaterIsOn ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
